/**
 * Prototype-only fictional data. No real clients, NPNs, or credentials.
 */
import type {
  BorLetterRow,
  ContactPerson,
  LicenseRow,
  OnboardingContact,
  OtrTransactionRow,
  PipelineClient,
  ProducerAppRow,
} from '../types/onboarding'

export const MOCK_CLIENT_NAME = 'Acme Specialty Insurance LLC'

export const MOCK_ASSIGNED_REP = 'Jordan Mercer (MISC)'

export const MOCK_SL_ADMIN: ContactPerson = {
  name: 'Taylor Brooks',
  title: 'Surplus Lines Administrator',
  phone: '(555) 010-4422',
  email: 't.brooks@example.com',
}

export const MOCK_ACCOUNTING: ContactPerson = {
  name: 'Casey Nguyen',
  title: 'Accounting Lead',
  phone: '(555) 010-8891',
  email: 'c.nguyen@example.com',
}

export const INITIAL_ONBOARDING_CONTACTS: OnboardingContact[] = [
  {
    id: 'c-1',
    role: 'SL Admin',
    firstName: 'Taylor',
    lastName: 'Brooks',
    phone: MOCK_SL_ADMIN.phone,
    email: MOCK_SL_ADMIN.email,
  },
  {
    id: 'c-2',
    role: 'Accounting',
    firstName: 'Casey',
    lastName: 'Nguyen',
    phone: MOCK_ACCOUNTING.phone,
    email: MOCK_ACCOUNTING.email,
  },
]

export const MOCK_LICENSES: LicenseRow[] = [
  {
    licenseeId: 'L1',
    licenseeFirstName: 'Kelly',
    licenseeLastName: 'Martinez',
    npn: '11002233',
    entity: 'Individual',
    fein: null,
    licenseState: 'CA',
    licenseNumber: 'SL-CA-771001',
    licenseType: 'Surplus Lines',
    residentState: true,
    status: 'Active',
    expirationDate: '2027-08-14',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
    companyName: 'Allstate Specialty Surplus LLC',
  },
  {
    licenseeId: 'L2',
    licenseeFirstName: '',
    licenseeLastName: 'Allstate Specialty Surplus LLC',
    npn: '22003344',
    entity: 'BusinessEntity',
    fein: '55-9911223',
    licenseState: 'CA',
    licenseNumber: 'SL-CA-882104',
    licenseType: 'Surplus Lines',
    residentState: false,
    status: 'Active',
    expirationDate: '2026-10-01',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
  },
  {
    licenseeId: 'L3',
    licenseeFirstName: 'Jordan',
    licenseeLastName: 'Ellis',
    npn: '33004455',
    entity: 'Individual',
    fein: null,
    licenseState: 'TX',
    licenseNumber: 'SL-TX-991204',
    licenseType: 'Surplus Lines',
    residentState: true,
    status: 'Active',
    expirationDate: '2027-03-15',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
    companyName: 'Allstate Specialty Surplus LLC',
  },
  {
    licenseeId: 'L4',
    licenseeFirstName: '',
    licenseeLastName: 'Summit Risk Partners Inc.',
    npn: '44005566',
    entity: 'BusinessEntity',
    fein: '88-1029384',
    licenseState: 'FL',
    licenseNumber: 'SL-FL-440012',
    licenseType: 'Surplus Lines',
    residentState: false,
    status: 'Active',
    expirationDate: '2026-11-01',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
  },
  {
    licenseeId: 'L5',
    licenseeFirstName: 'Priya',
    licenseeLastName: 'Shah',
    npn: '55006677',
    entity: 'Individual',
    fein: null,
    licenseState: 'NY',
    licenseNumber: 'SL-NY-220901',
    licenseType: 'Surplus Lines',
    residentState: true,
    status: 'Pending',
    expirationDate: '2027-01-20',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
    companyName: 'Summit Risk Partners Inc.',
  },
  {
    licenseeId: 'L6',
    licenseeFirstName: 'Morgan',
    licenseeLastName: 'Lee',
    npn: '66007788',
    entity: 'Individual',
    fein: null,
    licenseState: 'GA',
    licenseNumber: 'SL-GA-220877',
    licenseType: 'Surplus Lines',
    residentState: false,
    status: 'Active',
    expirationDate: '2028-01-20',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
    companyName: 'Allstate Specialty Surplus LLC',
  },
  {
    licenseeId: 'L7',
    licenseeFirstName: 'Alex',
    licenseeLastName: 'Rivera',
    npn: '77008899',
    entity: 'Individual',
    fein: null,
    licenseState: 'WA',
    licenseNumber: 'SL-WA-551102',
    licenseType: 'Surplus Lines',
    residentState: true,
    status: 'Active',
    expirationDate: '2027-09-30',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
    companyName: 'Summit Risk Partners Inc.',
  },
  {
    licenseeId: 'L8',
    licenseeFirstName: '',
    licenseeLastName: 'Desert Coastal MGA LLC',
    npn: '88009900',
    entity: 'BusinessEntity',
    fein: '12-8877665',
    licenseState: 'AZ',
    licenseNumber: 'SL-AZ-330044',
    licenseType: 'Surplus Lines',
    residentState: false,
    status: 'Active',
    expirationDate: '2026-06-18',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
  },
  {
    licenseeId: 'L9',
    licenseeFirstName: 'Sam',
    licenseeLastName: 'Okafor',
    npn: '99001122',
    entity: 'Individual',
    fein: null,
    licenseState: 'NV',
    licenseNumber: 'SL-NV-771200',
    licenseType: 'Surplus Lines',
    residentState: false,
    status: 'Expired',
    expirationDate: '2025-12-01',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
  },
  {
    licenseeId: 'L10',
    licenseeFirstName: 'Riley',
    licenseeLastName: 'Chen',
    npn: '10101010',
    entity: 'Individual',
    fein: null,
    licenseState: 'OR',
    licenseNumber: 'SL-OR-660033',
    licenseType: 'Surplus Lines',
    residentState: true,
    status: 'Active',
    expirationDate: '2028-04-22',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
  },
  {
    licenseeId: 'L11',
    licenseeFirstName: 'Dana',
    licenseeLastName: 'Whitfield',
    npn: '11112222',
    entity: 'Individual',
    fein: null,
    licenseState: 'IL',
    licenseNumber: 'SL-IL-441100',
    licenseType: 'Surplus Lines',
    residentState: true,
    status: 'Active',
    expirationDate: '2027-11-30',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
    companyName: 'Allstate Specialty Surplus LLC',
  },
  {
    licenseeId: 'L12',
    licenseeFirstName: 'Chris',
    licenseeLastName: 'Nguyen',
    npn: '12121212',
    entity: 'Individual',
    fein: null,
    licenseState: 'IL',
    licenseNumber: 'SL-IL-552200',
    licenseType: 'Surplus Lines',
    residentState: true,
    status: 'Active',
    expirationDate: '2028-02-14',
    source: 'NIPR_PDB',
    clientConfirmed: null,
    flaggedForReview: false,
    isDrlp: false,
    companyName: 'Summit Risk Partners Inc.',
  },
]

export const MOCK_BOR_STATES: BorLetterRow[] = [
  { stateCode: 'CA', status: 'not_started' },
  { stateCode: 'TX', status: 'not_started' },
  { stateCode: 'FL', status: 'not_started' },
]

export const MOCK_PRODUCERS: ProducerAppRow[] = [
  { npn: '88442211', name: 'Jane Smith', appStatus: 'not_started' },
  { npn: '77331100', name: 'Robert Johnson (entity)', appStatus: 'not_started' },
]

export const MOCK_PIPELINE: PipelineClient[] = [
  {
    clientId: 'C-1001',
    clientName: 'Acme Specialty Insurance LLC',
    serviceType: 'Both',
    stepsComplete: 3,
    stepsTotal: 15,
    transitionDate: '2026-05-12',
    lastActivity: '2026-04-26',
    status: 'InProgress',
    overdue: false,
  },
  {
    clientId: 'C-1002',
    clientName: 'Northwind Excess Partners',
    serviceType: 'SurplusLines',
    stepsComplete: 7,
    stepsTotal: 15,
    transitionDate: '2026-04-30',
    lastActivity: '2026-04-25',
    status: 'InProgress',
    overdue: true,
  },
  {
    clientId: 'C-1003',
    clientName: 'Contoso Risk Advisors Inc.',
    serviceType: 'Licensing',
    stepsComplete: 8,
    stepsTotal: 15,
    transitionDate: null,
    lastActivity: '2026-04-20',
    status: 'Complete',
  },
  {
    clientId: 'C-1004',
    clientName: 'Fabrikam Coastal Group',
    serviceType: 'Both',
    stepsComplete: 5,
    stepsTotal: 15,
    transitionDate: '2026-05-01',
    lastActivity: '2026-04-18',
    status: 'InProgress',
    anomaly: 'Volume below 4-week average',
  },
]

export const MOCK_OTR_ROWS: OtrTransactionRow[] = [
  {
    id: 'T1',
    invoiceNumber: 'INV-2026-44102',
    insuredName: 'Sample Holdings LLC',
    policyNumber: 'POL-X9-7721',
    homeState: 'TX',
    grossPremium: 50000,
    taxAmt: 1750,
    carrier: 'Example Carrier Co.',
    flaggedForReview: false,
  },
  {
    id: 'T2',
    invoiceNumber: 'INV-2026-44118',
    insuredName: 'Demo Property Trust',
    policyNumber: 'POL-M3-1102',
    homeState: 'FL',
    grossPremium: 12800,
    taxAmt: 512,
    carrier: 'Placeholder Insurance Group',
    flaggedForReview: true,
  },
]
